import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "../icons/icons";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";
import { DEFAULT_PAGE_SIZES } from "./pagination";

export interface KeysetPagerLabels {
	/** aria-label on the <nav>. */
	pagination?: string;
	/** "Showing 21 to 40 of 327" — receives raw numbers; format them yourself. */
	showing?: (range: { from: number; to: number; total: number }) => string;
	/** "Showing 21 to 40" — the total is not known (a reload mid-way through the list). */
	showingNoTotal?: (range: { from: number; to: number }) => string;
	/** The whole line at 0 rows. */
	empty?: ReactNode;
	rowsPerPage?: string;
	previousPage?: string;
	nextPage?: string;
}

export interface KeysetPagerProps {
	/** 1-based index of the first row on this page. */
	from: number;
	/** Rows on this page. */
	count: number;
	/** Matching rows in all — known from the first page; `null` when it is not. */
	total: number | null;
	hasPrevious: boolean;
	hasNext: boolean;
	onPrevious: () => void;
	onNext: () => void;
	pageSize: number;
	/** Omit to hide the page-size select. */
	onPageSizeChange?: (pageSize: number) => void;
	pageSizeOptions?: readonly number[];
	/** A page is loading: the buttons wait. */
	busy?: boolean;
	/** Anything drawn before the summary (the list's "Select all" box). */
	start?: ReactNode;
	labels?: KeysetPagerLabels;
	/** Only the top pager announces changes to screen readers. */
	position?: "top" | "bottom";
	className?: string;
}

/**
 * Keyset pager — Prev / Next over a cursor, for lists too long for OFFSET
 * (Rule 0: `OFFSET 50000` walks and discards 50,000 rows on every request).
 * Same look as `ListPagination` (the prototype's `.pgr`), minus the page
 * numbers a cursor cannot jump to: "Showing 21 to 40 of 327", the page-size
 * select, and ‹ ›.
 *
 * It owns no state: the app keeps the cursor (in the URL) and says whether
 * there is a page before / after this one.
 */
export function KeysetPager({
	from,
	count,
	total,
	hasPrevious,
	hasNext,
	onPrevious,
	onNext,
	pageSize,
	onPageSizeChange,
	pageSizeOptions = DEFAULT_PAGE_SIZES,
	busy = false,
	start,
	labels,
	position = "top",
	className,
}: KeysetPagerProps) {
	const { pagination: strings } = useUiStrings();
	const to = from + Math.max(0, count) - 1;
	let summary: ReactNode;
	if (count === 0 && !hasPrevious) summary = labels?.empty ?? strings.empty;
	else if (total !== null) summary = (labels?.showing ?? strings.showing)({ from, to, total });
	else summary = (labels?.showingNoTotal ?? strings.showingNoTotal)({ from, to });

	// Same button as ListPagination's (.pgn__b).
	const btn =
		"grid h-[2.125rem] min-w-[2.125rem] cursor-pointer place-items-center rounded-[0.5rem] border border-gms-line bg-base-100 px-[0.5rem] text-[0.8125rem] font-semibold text-gms-ink-2 outline-none transition-colors hover:enabled:border-gms-navy hover:enabled:text-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-default disabled:opacity-35";

	return (
		<nav
			aria-label={labels?.pagination ?? strings.label}
			aria-busy={busy || undefined}
			className={cx("flex flex-wrap items-center gap-x-[0.875rem] gap-y-[0.5rem] text-[0.8125rem] text-gms-ink-3", className)}
		>
			{start}
			{onPageSizeChange && (
				<select
					aria-label={labels?.rowsPerPage ?? strings.rowsPerPage}
					className="h-[2.25rem] cursor-pointer rounded-[0.5rem] border border-gms-line bg-base-100 pr-[0.25rem] pl-[0.5rem] text-[0.8125rem] text-gms-ink-2 tabular-nums outline-none focus-visible:border-gms-navy"
					value={pageSize}
					onChange={(e) => onPageSizeChange(Number(e.target.value))}
				>
					{pageSizeOptions.map((n) => (
						<option key={n} value={n}>
							{strings.perPage(n)}
						</option>
					))}
				</select>
			)}
			<p className="min-w-[8.75rem] flex-1 tabular-nums" aria-live={position === "top" ? "polite" : undefined}>
				{summary}
			</p>
			<div className="flex items-center gap-[0.375rem]">
				<button
					type="button"
					className={btn}
					onClick={onPrevious}
					disabled={!hasPrevious || busy}
					aria-label={labels?.previousPage ?? strings.previousPage}
					title={labels?.previousPage ?? strings.previousPage}
				>
					<ChevronLeft className="size-[1rem]" />
				</button>
				<button
					type="button"
					className={btn}
					onClick={onNext}
					disabled={!hasNext || busy}
					aria-label={labels?.nextPage ?? strings.nextPage}
					title={labels?.nextPage ?? strings.nextPage}
				>
					<ChevronRight className="size-[1rem]" />
				</button>
			</div>
		</nav>
	);
}
